import { REGEX } from "../constants/regex";
import { checkDomain } from "../util/share";
import { GetLocateLocation } from "./GetLocateLocation";
import { LocateAddressBumper } from './locateAddressBumper';

/**
 * Helper class for DOM related utilities
 */
class DomUtils {
  /**
   * Shows an error message for a field
   */
  static showError(fieldId: string, message: string = ''): void {
    const field = document.getElementById(fieldId);
    const errorElement = document.getElementById(`${fieldId}-error-msg`);
    
    if (field) field.classList.add("invalid-field");
    if (errorElement) {
      if (message) errorElement.textContent = message;
      errorElement.classList.remove('hidden');
    }
  }

  /**
   * Hides an error message for a field
   */
  static hideError(fieldId: string): void {
    const field = document.getElementById(fieldId);
    const errorElement = document.getElementById(`${fieldId}-error-msg`);
    
    if (field) field.classList.remove("invalid-field");
    if (errorElement) errorElement.classList.add('hidden');
  }

  /**
   * Gets the selected service type
   */
  static getSelectedServiceType(): boolean {
    const serviceTypeRadios = document.querySelectorAll('.find-local-modal .glass-type-container input[name="glass-type"]');
    return Array.from(serviceTypeRadios).some(radio => (radio as HTMLInputElement).checked);
  }
}

export class SearchLocalSite {
  private readonly headerBtnZipcode: HTMLButtonElement | null;
  private localSearchInput: HTMLInputElement | null;
  private readonly locateLocation: GetLocateLocation;

  constructor() {
    this.headerBtnZipcode = document.querySelector('.find-local-modal form .form-cta .primary-btn');
    this.localSearchInput = document.getElementById('local-search-modal') as HTMLInputElement;
    this.locateLocation = GetLocateLocation.getInstance();

    this.initializeEventListeners();
    this.initializeErrorMessageElement();
    this.initializeCloseButtonHandlers();
  }

  private initializeEventListeners(): void {
    // Header button click
    if (this.headerBtnZipcode) {
      this.headerBtnZipcode.addEventListener("click", this.handleButtonClick.bind(this));
    }

    // Radio button changes
    const radioButtons = document.querySelectorAll('.find-local-modal .glass-type-container .glass-type-radio');
    radioButtons?.forEach((radio) => {
      radio.addEventListener('change', this.handleServiceTypeChange.bind(this));
    });

    // Input field events
    if (this.localSearchInput) {
      this.localSearchInput.addEventListener('input', this.handleSearchInput.bind(this));
      
      if (this.headerBtnZipcode) {
        this.localSearchInput.addEventListener('keydown', (event: KeyboardEvent) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            event.stopPropagation();
            this.handleEnterKey();
          }
        });
      }
    }
  }

  private initializeErrorMessageElement(): void {
    if (!this.localSearchInput) return;
    
    const errorMessageSpan = document.createElement('span');
    errorMessageSpan.id = 'modal-error_message';
    errorMessageSpan.className = 'error-msg block';
    errorMessageSpan.style.display = 'none';

    const formCtaDiv = document.querySelector(".step-1 .form-cta");
    const formCtaParent = formCtaDiv?.parentNode;
    
    if (formCtaParent) {
      formCtaParent.insertBefore(errorMessageSpan, formCtaDiv);
    }
  }

  private initializeCloseButtonHandlers(): void {
    window.addEventListener('load', () => {
      const findLocalButtons = document.querySelectorAll('.close-modal-btn');
      
      findLocalButtons.forEach(button => {
        if (button) {
          button.addEventListener('click', (e) => this.handleCloseButtonClick(e));
        }
      });
    });
  }

  private handleSearchInput(): void {
    if (!this.localSearchInput) return;
    
    const isCaDomain = checkDomain() === 'ca';
    const zipPattern = isCaDomain ? REGEX.caSendZip : REGEX.sendZip;
    const isInvalidZip = this.localSearchInput.value !== '' && !zipPattern.test(this.localSearchInput.value.trim());

    if (isInvalidZip) {
      DomUtils.showError('local-search-modal', 'Invalid zip code format.');
    } else {
      DomUtils.hideError('local-search-modal');
    }

    this.updateStepTextVisibility('block', 'none');
  }

  private handleEnterKey(): void {
    if (this.localSearchInput?.value) {
      this.headerBtnZipcode?.click();
    } else {
      DomUtils.showError('local-search-modal', 'Please enter a zip code');
    }
  }

  private handleCloseButtonClick(event: Event): void {
    event.preventDefault();
    
    if (this.localSearchInput) {
      this.localSearchInput.value = '';
    }
    
    this.updateStepTextVisibility('block', 'none');
    
    // Handle sidebar menu state
    const sideBarMenu = document.querySelector(".menu-sidebar-mobile");
    if (sideBarMenu?.getAttribute('aria-modal') === "true") {
      document.body.classList.add('overflow-hidden');
    }
  }

  /**
   * Updates the visibility of step text elements
   */
  /**
   * Handles the button click event for form submission
   */
  public handleButtonClick(event: Event): void {
    event.stopPropagation();
    event.preventDefault();

    // Always use the localSearchInput value since it's the main input field
    let zipValue = this.localSearchInput?.value?.trim();
    
    if (!zipValue) {
      console.log('No zip code provided');
      DomUtils.showError('local-search-modal', 'Please enter a zip code');
      return;
    }

    // Format for Canadian zip codes if needed
    const isCanadianSite = this.isCanadianSite();
    
    if (isCanadianSite && zipValue.indexOf(" ") === -1) {
      zipValue = zipValue.replace(/^(.{3})(.*)$/, "$1 $2");
    }


    this.saveZipCode(zipValue);
    
    // Validate the form
    const isValidZip = this.validateForm(zipValue);

    if (isValidZip) {
      const locateAddressBumper = new LocateAddressBumper();
      this.locateLocation.locateLocation("desk");
    } else {
      console.log('Form validation failed');
    }
  }

  /**
   * Handles service type radio button changes
   */
  public handleServiceTypeChange(): void {
    const serviceTypeContainer = document.querySelector('.find-local-modal .glass-type-container') as HTMLElement;
    if (!serviceTypeContainer) return;

    if (this.isServiceTypeRequired()) {
      if (this.isServiceTypeSelected()) {
        DomUtils.hideError('local-search-modal');
      } else {
        DomUtils.showError('local-search-modal', 'Please select a service type');
      }
    }
  }

  private getFormattedZipCode(buttonId: string, zipInput: HTMLInputElement): string | undefined {
    const localSearchInputVal = this.localSearchInput?.value;
    let zipValue = buttonId === "local-search-modal" ? localSearchInputVal : zipInput?.value;
    
    if (!zipValue) return undefined;

    // Format Canadian zip code if needed
    const isCanadianSite = this.isCanadianSite();
    if (isCanadianSite && zipValue.indexOf(" ") === -1) {
      zipValue = zipValue.replace(/^(.{3})(.*)$/, "$1 $2");
    }

    return zipValue;
  }

  private isCanadianSite(): boolean {
    const domainName = location.hostname.toLowerCase();
    return domainName.includes("-ca-") || domainName.endsWith(".ca") || domainName.includes("-ca.");
  }

  private saveZipCode(zipValue: string): void {
    localStorage.setItem('zipcode', zipValue);
    const inputZipCode = document.querySelector('.step-2 #modal-zipcode') as HTMLInputElement;
    if (inputZipCode) {
      inputZipCode.value = zipValue;
    }
  }

  private validateForm(zipValue: string): boolean {
    if (!this.localSearchInput) return false;

    // Clear all previous errors
    document.querySelectorAll('.error-msg').forEach(e => (e as HTMLElement).classList.add('hidden'));

    // Validate service type if required
    if (this.isServiceTypeRequired() && !this.isServiceTypeSelected()) {
      const errorMsg = document.getElementById('local-search-modal-error-msg');
      if (errorMsg) {
        errorMsg.textContent = 'Please select a service type';
        errorMsg.classList.remove('hidden');
      }
      return false;
    }

    // Validate zip code format
    const isCaDomain = checkDomain() === 'ca';
    const zipPattern = isCaDomain ? REGEX.caSendZip : REGEX.sendZip;
    const isValidZip = zipPattern.test(zipValue.trim());

    if (this.localSearchInput.required && !isValidZip) {
      const errorMsg = document.getElementById('local-search-modal-error-msg');
      if (errorMsg) {
        errorMsg.textContent = 'Invalid zip code format.';
        errorMsg.classList.remove('hidden');
      }
      return false;
    }

    return true;
  }

  private isServiceTypeRequired(): boolean {
    const serviceTypeContainer = document.querySelector('.find-local-modal .glass-type-container');
    return serviceTypeContainer?.hasAttribute('required') ?? false;
  }

  private isServiceTypeSelected(): boolean {
    return DomUtils.getSelectedServiceType();
  }

  private updateStepTextVisibility(text1Display: string, text2Display: string): void {
    const text1 = document.getElementById("step1-text1") as HTMLElement;
    const text2 = document.getElementById("step1-text2") as HTMLElement;
    
    if (text1) text1.style.display = text1Display;
    if (text2) text2.style.display = text2Display;
  }
}

// Initialize the search functionality
  const searchLocalSite = new SearchLocalSite();
